import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { formatMinor } from './use-pricing';

export type ReceiptLifecycleState =
  | 'issued'
  | 'partially_returned'
  | 'fully_returned'
  | 'locked'
  | 'expired';

export type ReceiptKind = 'sale' | 'refund' | 'void' | 'parked' | 'x_report' | 'z_report' | 'copy';

export type ReceiptSummary = {
  id: string;
  clientUuid: string;
  issuingTerminalId: string;
  cashierId: string | null;
  /**
   * RL-513: who authorised the receipt for `cashierId` — the supervisor or
   * second cashier behind a refund. Absent from a Back Office that predates it.
   */
  approverCashierId?: string | null;
  approverEmployeeCode?: string | null;
  shiftId: string | null;
  receiptType: ReceiptKind;
  lifecycleState: ReceiptLifecycleState;
  subtotalMinor: string;
  totalDiscountMinor: string | null;
  totalMinor: string;
  countryPackId: string | null;
  countryPackVersion: string | null;
  originalReceiptId: string | null;
  lockedByTerminalId: string | null;
  lockedAt: string | null;
  lockedUntil: string | null;
  finalisedAt: string;
  createdAt: string;
  updatedAt: string;
  /** YUKI-39: the row's own currency — the list used to hardcode EUR. */
  currency: string | null;
  /** Present only on imported 1C history. */
  legacyFiscalLink: string | null;
  legacyDepartmentCode: string | null;
  isLegacy: boolean;
  /** Terminal display name, so the list need not show an opaque id. */
  terminalLabel?: string | null;
};

export type ReceiptLine = {
  sku: string | null;
  name: string;
  quantity: number;
  unitOfMeasure: string;
  lineTotalMinor: string;
  unitPriceMinor: string | null;
  discountMinor: string | null;
  taxCategory: string;
  taxRatePercent: number;
  /** FN-225: sold without its optional excise stamp. Absent on every other line. */
  exciseStampSkipped?: boolean;
};

export type ReturnAllocation = {
  id: string;
  originalReceiptId: string;
  originalLineSku: string;
  originalLineIndex: number;
  returnReceiptId: string;
  quantityReturned: string;
  refundAmountMinor: string;
  createdAt: string;
};

export type ReceiptDetail = ReceiptSummary & {
  lines: ReceiptLine[];
  /** The fiscal block the till stored; any shape (read with `readFiscalBlock`). */
  fiscal: unknown;
  /** PFC-21: the receipt as printed (the receipt IR), when the till sent it. */
  receiptDocument?: unknown;
  terminalLabel: string | null;
  cashierDisplayName: string | null;
  /** RL-513: the approver's name, when Back Office knows the approver. */
  approverDisplayName?: string | null;
  returnAllocations: ReturnAllocation[];
  /**
   * SNX-1124: the tender legs as the till froze them (masked card data only),
   * with `settlement` on a card leg whose till reports how it ended. Null for
   * a till that sent no legs; absent from an older Back Office.
   */
  payments?: ReceiptPayment[] | null;
};

export type ReceiptPayment = {
  method: string;
  amountMinor: string;
  cardNumber?: string;
  authorizationCode?: string;
  rrn?: string;
  paymentTerminalId?: string;
  extensions?: {
    'snx.host'?: {
      stan?: string;
      merchantId?: string;
      cardScheme?: string;
      maskedPAN?: string;
      hostTimestamp?: string;
      attemptId?: string;
      finalStatus?: string;
    };
  };
  settlement?: { status: 'settled' | 'voided' | 'unresolved'; reportedAt: string | null } | null;
};

export type ReceiptListResponse = {
  items: ReceiptSummary[];
  total: number;
  page: number;
  pageSize: number;
};

export type ReceiptListQuery = {
  page?: number;
  pageSize?: number;
  lifecycleState?: ReceiptLifecycleState[];
  receiptType?: ReceiptKind[];
  /** YUKI-39: imported 1C history only, or exclude it. */
  legacy?: 'only' | 'exclude';
  /**
   * RL-377: the `finalisedAt` window, as instants. The API has filtered on
   * these since receipts shipped (`receipts/dto.ts`); only the client had no
   * way to ask, which is what the dashboard's sales drill-down needs.
   */
  finalisedFrom?: string;
  finalisedTo?: string;
  /** SNX-1124: the receipt or fiscal number printed on the receipt, exact. */
  receiptNumber?: string;
};

const LIST_KEY = ['receipts'] as const;
const ONE_KEY = (id: string) => ['receipts', id] as const;

export function useReceipts(query: ReceiptListQuery = {}) {
  return useQuery({
    queryKey: [...LIST_KEY, query] as const,
    queryFn: async (): Promise<ReceiptListResponse> => {
      const params: Record<string, string | number> = {};
      if (query.page !== undefined) params.page = query.page;
      if (query.pageSize !== undefined) params.pageSize = query.pageSize;
      if (query.lifecycleState !== undefined && query.lifecycleState.length > 0) {
        params['filter[lifecycleState]'] = query.lifecycleState.join(',');
      }
      if (query.receiptType !== undefined && query.receiptType.length > 0) {
        params['filter[receiptType]'] = query.receiptType.join(',');
      }
      if (query.legacy !== undefined) params['filter[legacy]'] = query.legacy;
      if (query.finalisedFrom !== undefined) params['filter[finalisedFrom]'] = query.finalisedFrom;
      if (query.finalisedTo !== undefined) params['filter[finalisedTo]'] = query.finalisedTo;
      if (query.receiptNumber !== undefined && query.receiptNumber.trim() !== '')
        params['filter[receiptNumber]'] = query.receiptNumber.trim();
      const res = await apiClient.get<ReceiptListResponse>('/receipts', { params });
      return res.data;
    },
  });
}

export function useReceipt(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['receipts', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<ReceiptDetail> => {
      const res = await apiClient.get<ReceiptDetail>(`/receipts/${id}`);
      return res.data;
    },
  });
}

export function useLockReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; reason: string }) => {
      const res = await apiClient.post<ReceiptSummary>(`/receipts/${vars.id}/lock`, {
        reason: vars.reason,
      });
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(vars.id) });
    },
  });
}

export function useUnlockReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<ReceiptSummary>(`/receipts/${id}/unlock`, {});
      return res.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(id) });
    },
  });
}

export const STATE_LABEL: Record<ReceiptLifecycleState, string> = {
  issued: 'Issued',
  partially_returned: 'Partially returned',
  fully_returned: 'Fully returned',
  locked: 'Locked (fraud hold)',
  expired: 'Expired',
};

export const KIND_LABEL: Record<ReceiptKind, string> = {
  sale: 'Sale',
  refund: 'Refund',
  void: 'Void',
  parked: 'Parked',
  x_report: 'X-Report',
  z_report: 'Z-Report',
  copy: 'Copy',
};

/**
 * Catalogue keys for the two maps above. The maps stay as the baked English, so
 * a caller resolves a label as `t(STATE_LABEL_KEY[s], STATE_LABEL[s])`. These
 * are the singular badge words; `receipts.list.state.*` are the plural filter
 * chips and stay separate.
 */
export const STATE_LABEL_KEY: Record<ReceiptLifecycleState, string> = {
  issued: 'receipts.vocab.stateIssued',
  partially_returned: 'receipts.vocab.statePartiallyReturned',
  fully_returned: 'receipts.vocab.stateFullyReturned',
  locked: 'receipts.vocab.stateLocked',
  expired: 'receipts.vocab.stateExpired',
};

export const KIND_LABEL_KEY: Record<ReceiptKind, string> = {
  sale: 'receipts.vocab.kindSale',
  refund: 'receipts.vocab.kindRefund',
  void: 'receipts.vocab.kindVoid',
  parked: 'receipts.vocab.kindParked',
  x_report: 'receipts.vocab.kindXReport',
  z_report: 'receipts.vocab.kindZReport',
  copy: 'receipts.vocab.kindCopy',
};

/**
 * RL-363: minor units to a bare major-unit string ("1234" -> "12.34").
 *
 * This used to go `Number(minor) / 100` and `toFixed(2)` — a double on the way
 * to the two screens an operator reconciles against. It now delegates to the
 * string-only formatter, so the digits never leave integer arithmetic whatever
 * their size. No currency glyph: the receipt detail appends the code itself.
 * A value that is not an integer string is shown as-is, as before.
 */
export function formatMinorAsString(minor: string | null): string {
  if (minor === null) return '—';
  return formatMinor(minor);
}
